import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  Divider,
  SxProps,
  Theme,
  Box,
  useTheme,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AppButton from "./AppButton";

interface DropdownOption {
  label: string;
  icon?: React.ReactNode;
}

interface AppButtonDropdownProps {
  label: React.ReactNode;
  options: DropdownOption[];
  onSelect: (option: string) => void;
  customStyles?: SxProps<Theme>;
  disabled?: boolean;
}

const AppButtonDropdown = ({
  label,
  options = [],
  onSelect,
  customStyles = {},
  disabled = false,
}: AppButtonDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();

  const handleClick = (event?: React.MouseEvent<HTMLElement>) => {
    if (event) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (option: string) => {
    handleClose();
    onSelect(option);
  };

  return (
    <>
      <AppButton
        label={label}
        onClick={handleClick}
        endIcon={
          <Box display="flex" alignItems="center" gap={1}>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                height: 18,
                paddingRight: 1,
                paddingLeft: 0.1,
                borderColor: theme.palette.customColors.white,
                opacity: 0.7,
              }}
            />
            <ArrowDropDownIcon
              sx={{
                fontSize: "1.2rem",
                transition: "transform 0.2s ease-in-out",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </Box>
        }
        disabled={disabled}
        customStyles={{
          background: `linear-gradient(135deg, ${theme.palette.customColors.purple} 0%, ${theme.palette.customColors.purpleMain} 100%)`,
          color: theme.palette.customColors.white,
          borderRadius: "12px",
          minWidth: "150px",
          minHeight: "40px",
          textTransform: "none",
          display: "flex",
          alignItems: "center",
          paddingRight: 4.5,
          paddingLeft: 3,
          fontWeight: 600,
          fontSize: "0.9rem",
          boxShadow: "0 4px 12px rgba(100, 85, 237, 0.3)",
          border: "none",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            background: `linear-gradient(135deg, ${theme.palette.customColors.purpleMain} 0%, ${theme.palette.customColors.purple} 100%)`,
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(100, 85, 237, 0.4)",
          },
          "&:active": {
            transform: "translateY(0px)",
            boxShadow: "0 2px 8px rgba(100, 85, 237, 0.3)",
          },
          ...customStyles,
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              boxShadow:
                "0 12px 40px rgba(0, 0, 0, 0.25), 0 4px 16px rgba(0, 0, 0, 0.15)",
              border: `1px solid ${theme.palette.customColors.lightBorder}`,
              backgroundColor: theme.palette.customColors.white,
              overflow: "hidden",
              minWidth: "200px",
              marginTop: "8px",
              "& .MuiMenuItem-root": {
                padding: "10px 16px",
                fontSize: "0.9rem",
                fontWeight: 500,
                color: theme.palette.customColors.blackBorder,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: theme.palette.customColors.lightPurple,
                  color: theme.palette.customColors.purpleMain,
                },
                "&:first-of-type": {
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                },
                "&:last-of-type": {
                  borderBottomLeftRadius: "12px",
                  borderBottomRightRadius: "12px",
                },
              },
            },
          },
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        {options.map((option, index) => (
          <MenuItem
            key={index}
            onClick={() => handleSelect(option.label)}
            sx={{
              fontSize: "0.9rem",
              fontWeight: 500,
              color: theme.palette.customColors.blackBorder,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightPurple,
                color: theme.palette.customColors.purpleMain,
              },
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={1.5}
              sx={{
                fontSize: "0.9rem",
                fontWeight: 500,
                width: "100%",
              }}
            >
              {option.icon && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "20px",
                    "& svg": {
                      fontSize: "1.1rem",
                      color: "inherit",
                    },
                  }}
                >
                  {option.icon}
                </Box>
              )}
              <Box sx={{ flex: 1, fontSize: "0.9rem" }}>{option.label}</Box>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default AppButtonDropdown;
